import { PencilIcon } from "lucide-react";
import { useRef, useState } from "react";
import { FieldPath, SubmitHandler } from "react-hook-form";
import { useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import { useMutation } from "urql";

import {
    TournamentForm,
    TournamentFormRef,
    convertFormToGraphQL,
    convertGraphQLToForm,
} from "@/components/forms/TournamentForm";
import { TournamentInput, zTournamentInput } from "@/components/forms/validation/tournament";
import { buttonVariants } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";
import { graphql } from "@/gen";
import { TournamentT } from "@/routes/tournament/queries";
import { cn } from "@/utils";

const updateTournamentMutation = graphql(`
    mutation updateTournament($input: UpdateTournamentInput!) {
        updateTournament(input: $input) {
            id
            ...useTournament_Tournament
        }
    }
`);

export const TournamentEditDialog = () => {
    const [_res, updateTournament] = useMutation(updateTournamentMutation);
    const { tournament } = useOutletContext<{ tournament: TournamentT }>();
    const [isOpen, setIsOpen] = useState(false);
    const formRef = useRef<TournamentFormRef>(null);

    const onSubmit: SubmitHandler<TournamentInput> = async input => {
        const standingsCalculation = convertFormToGraphQL(input.standingsCalculation);

        const result = await updateTournament({
            input: {
                id: tournament.id,
                title: input.title,
                shortTitle: input.shortTitle,
                description: input.description,
                location: input.location,
                startAt: input.startAt.toISOString(),
                endAt: input.endAt.toISOString(),
                heroImageId: input.heroImageId,
                thumbnailImageId: input.thumbnailImageId,
                badgeImageId: input.badgeImageId,
                format: input.format,
                players: input.players.map(player => {
                    return {
                        playerId: player.id,
                    };
                }),
                previousWinnerPlayerId: input.previousWinnerPlayerId,
                isInternalOnly: input.isInternalOnly,
                isHidden: input.isHidden,
                manualStandings: input.manualStandings,
                manualStandingsList: input.manualStandings
                    ? input.manualStandingsList
                          .filter(entry => entry.player != null)
                          .map(entry => ({
                              playerId: entry.player!.id,
                              points: entry.points,
                              rankPoints: entry.rankPoints,
                          }))
                    : null,
                countryStandingsEnabled: input.countryStandingsEnabled,
                countryStandingsTopPlayers: input.countryStandingsTopPlayers,
                standingsCalculation: standingsCalculation ?? null,
                evaluationSurgeProtection: input.evaluationSurgeProtection,
                chesscomInternalSlug: input.chesscomInternalSlug,
            },
        });

        if (result.error) {
            toast.error("Unable to update tournament", { description: result.error.message });
            for (const error of result.error.graphQLErrors) {
                formRef.current?.setError(error.name as FieldPath<TournamentInput>, {
                    message: error.message,
                });
            }
        } else {
            toast.success("Tournament was updated");
            setIsOpen(false);
        }
    };

    const defaultValues = zTournamentInput.parse({
        ...tournament,
        standingsCalculation: convertGraphQLToForm(tournament.standingsCalculation),
    });

    return (
        <Dialog open={isOpen} onOpenChange={() => setIsOpen(!isOpen)}>
            <DialogTrigger className={cn(buttonVariants())}>
                <PencilIcon />
            </DialogTrigger>
            <DialogContent
                className="max-h-[80vh] w-full max-w-5xl overflow-y-auto"
                aria-describedby={undefined}
            >
                <DialogHeader>
                    <div className="mr-4 flex items-center justify-between">
                        <DialogTitle>Edit tournament</DialogTitle>
                    </div>
                </DialogHeader>
                <TournamentForm onSubmit={onSubmit} defaultValues={defaultValues} />
            </DialogContent>
        </Dialog>
    );
};
